import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet } from 'react-native';
import { View } from 'react-native';

import { useToast } from '@fhs/auth/src/hooks';
import { Schema } from '@fhs/client';
import { AddedToCart } from '@fhs/menu/src/components/guide/added-to-cart';
import { ComboSelectionProvider } from '@fhs/menu/src/state/combo-selection';
import { MenuPickerProvider, useMenuPicker } from '@fhs/menu/src/state/menu-picker';
import { useTransformForAddToCart } from '@fhs/menu/src/utils/use-transform-for-add-to-cart';
import { ProductListTile, ProductListTileProps, tokens } from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import { ICartEntry } from '@rbi-ctg/menu';

import { useOfferCart } from '../../state/offer-guide-cart-state';

function HandleSelectedItem({
  addItemToSimplyOfferCart,
}: {
  addItemToSimplyOfferCart: (cartEntry: ICartEntry) => void;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const { setSelectedEligibleItem } = useOfferCart();
  const menuPicker = useMenuPicker();
  const toast = useToast();

  const transformForAddToCart = useTransformForAddToCart();

  const handleAddToCart = useCallback(async () => {
    try {
      setIsLoading(true);
      const itemId = menuPicker.selectedPickerAspectOption?.itemId;
      if (itemId) {
        const cartEntry = await transformForAddToCart({
          itemId,
          quantity: 1,
          isCombo: false,
          pickerSelections: menuPicker.pickerSelections,
          resolvedCustomizationSelections: menuPicker.resolvedCustomizationSelections,
          selectedPickerAspectOption: menuPicker.selectedPickerAspectOption,
        });

        if (cartEntry) {
          setTimeout(() => {
            addItemToSimplyOfferCart(cartEntry);
          }, 500);
        }
      }
    } catch (error) {
      toast.show({
        text: 'Something went wrong',
        variant: 'negative',
      });

      setSelectedEligibleItem(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [
    transformForAddToCart,
    menuPicker.pickerSelections,
    menuPicker.resolvedCustomizationSelections,
    menuPicker.selectedPickerAspectOption,
    addItemToSimplyOfferCart,
    setSelectedEligibleItem,
    toast,
  ]);

  const handleAddToCartRef = useBoxRef(handleAddToCart);

  useEffect(() => {
    if (!menuPicker.isLoading) {
      handleAddToCartRef.current();
    }
  }, [menuPicker.isLoading, handleAddToCartRef]);

  if (isLoading || menuPicker.isLoading) {
    return (
      <View style={styles.itemOverlay}>
        <ActivityIndicator size="large" color={tokens.colors.$success} />
      </View>
    );
  }
  return (
    <View style={styles.itemOverlay}>
      <AddedToCart />
    </View>
  );
}

export interface ItemTileProps {
  isFirstColumn: boolean;
  isLastColumn: boolean;
  selectedEligibleItem?: Schema['MenuSectionItem']['type'];
  productListTileProps: ProductListTileProps;
  slug: string;
  id: string;
  addItemToSimplyOfferCart: (cartEntry: ICartEntry) => void;
}

export function ItemTile({
  isFirstColumn,
  isLastColumn,
  selectedEligibleItem,
  productListTileProps,
  slug,
  id,
  addItemToSimplyOfferCart,
}: ItemTileProps) {
  const isSelected = selectedEligibleItem && selectedEligibleItem?.id === id;

  const content = (
    <View
      style={[styles.item, isFirstColumn && styles.firstColumn, isLastColumn && styles.lastColumn]}
    >
      <ProductListTile {...productListTileProps} />
    </View>
  );

  if (isSelected) {
    return (
      <MenuPickerProvider slug={slug}>
        <ComboSelectionProvider>
          {content}
          <HandleSelectedItem addItemToSimplyOfferCart={addItemToSimplyOfferCart} />
        </ComboSelectionProvider>
      </MenuPickerProvider>
    );
  }

  return content;
}

const styles = StyleSheet.create({
  item: {
    flex: 1,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 12,
  },
  firstColumn: {
    paddingLeft: 0,
  },
  lastColumn: {
    paddingRight: 0,
  },
  itemOverlay: {
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});
